import { Appointment, AppointmentStatus, Part, TaskStatus } from '../../../types';
import { shopWallClockDate, shopWallClockToEpoch, toDateKey } from '../../../utils/shopTime';

/** "BAY 01" — two digits so a row of bay cards lines up. */
export const bayLabel = (bay: number) => `BAY ${String(bay).padStart(2, '0')}`;

const DONE_TASKS = new Set<TaskStatus>([TaskStatus.COMPLETED, TaskStatus.FINISHED]);

/** Tasks done out of tasks that still count (cancelled ones drop out). */
export const taskProgress = (job: Appointment) => {
  const tasks = (job.tasks ?? []).filter((task) => task.status !== TaskStatus.CANCELLED);
  const done = tasks.filter((task) => DONE_TASKS.has(task.status)).length;
  return { done, total: tasks.length, percent: tasks.length ? Math.round((done / tasks.length) * 100) : 0 };
};

export const vehicleOf = (job: Pick<Appointment, 'vehicleYear' | 'vehicleModel'>) =>
  [job.vehicleYear, job.vehicleModel].filter(Boolean).join(' ');

export const mechanicNameOf = (job: Appointment) => job.mechanic?.user?.name ?? null;

export const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');

export const greetingFor = (hour: number) =>
  hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

/** Jobs that can be dropped into an empty bay: today's, not finished, not already in one. */
export const bayCandidates = (today: Appointment[], floorJobs: Appointment[]) => {
  const inBay = new Set(floorJobs.map((job) => job.id));
  return today.filter(
    (job) =>
      !inBay.has(job.id) &&
      [AppointmentStatus.PENDING, AppointmentStatus.CONFIRMED, AppointmentStatus.IN_PROGRESS].includes(
        job.status,
      ),
  );
};

/** Out of stock, or under half the reorder point, reads as urgent rather than low. */
export const isCriticallyLow = (part: Part) => {
  const onHand = Number(part.onHand);
  return onHand <= 0 || onHand <= Number(part.reorderPoint) / 2;
};

/** Most urgent first: the smallest share of the reorder point still on the shelf. */
export const byUrgency = (a: Part, b: Part) => {
  const share = (part: Part) => Number(part.onHand) / Math.max(Number(part.reorderPoint), 1);
  return share(a) - share(b);
};

/**
 * Shop-local "now", plus the epoch bounds of today and of this Monday-to-Sunday
 * week — read on the shop's clock, never the viewer's.
 */
export const shopCalendar = (timeZone: string | undefined, nowSeconds = Math.floor(Date.now() / 1000)) => {
  const now = shopWallClockDate(nowSeconds, timeZone);
  const todayKey = toDateKey(now);
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7));
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);

  return {
    now,
    todayKey,
    day: {
      from: shopWallClockToEpoch(todayKey, '00:00:00', timeZone),
      to: shopWallClockToEpoch(todayKey, '23:59:59', timeZone),
    },
    week: {
      from: shopWallClockToEpoch(toDateKey(monday), '00:00:00', timeZone),
      to: shopWallClockToEpoch(toDateKey(sunday), '23:59:59', timeZone),
    },
  };
};
